/**
 * React Hook for Keyboard Shortcuts
 * 
 * Provides a clean React interface for the keyboard shortcut system.
 * Handles initialization, cleanup, and state management.
 */

import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Grid } from 'antd';
import { keyboardManager } from '../manager/KeyboardShortcutManager';

const { useBreakpoint } = Grid;

/**
 * Custom hook for handling keyboard shortcuts
 * Currently supports:
 * - Ctrl+B: Toggle navigation sidebar
 * - Ctrl+Shift+?: Show keyboard shortcuts help
 * - Double Shift: Quick navigation search
 * - Ctrl+Alt+C: Create new chat agent
 * - Ctrl+Alt+V: Create new voice agent
 * - Ctrl+Alt+D: Upload document to knowledge base
 */
export const useKeyboardShortcuts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navCollapsed = useSelector(state => state.theme.navCollapsed);
  const mobileNav = useSelector(state => state.theme.mobileNav);
  const screens = useBreakpoint();
  
  // State for modals
  const [showHelp, setShowHelp] = useState(false);
  const [showNavigationSearch, setShowNavigationSearch] = useState(false);
  
  // Update context whenever dependencies change
  const updateContext = useCallback(() => {
    keyboardManager.updateContext({
      dispatch,
      navigate,
      location,
      navCollapsed,
      mobileNav,
      screens,
      setShowHelp,
      setShowNavigationSearch
    });
  }, [dispatch, navigate, location, navCollapsed, mobileNav, screens]);
  
  // Initialize keyboard manager
  useEffect(() => {
    const context = {
      dispatch,
      navigate,
      location,
      navCollapsed,
      mobileNav,
      screens,
      setShowHelp,
      setShowNavigationSearch
    };
    
    keyboardManager.init(context);
    
    // Cleanup on unmount
    return () => {
      keyboardManager.destroy();
    };
  }, [dispatch, navigate, location, navCollapsed, mobileNav, screens]); // Include dependencies
  
  // Update context when dependencies change
  useEffect(() => {
    updateContext();
  }, [updateContext]);
  
  return {
    showHelp,
    setShowHelp,
    showNavigationSearch,
    setShowNavigationSearch,
    // Expose manager for advanced use cases
    keyboardManager
  };
};